import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useRegulatoryVelocity,
  type RegulatoryFilters,
} from '@/hooks/useRegulatoryQueries'

interface Props {
  filters: Pick<RegulatoryFilters, 'jurisdiction' | 'timeframe'>
}

// Prior window is symmetric to the recent window (equal-length comparison),
// matching the server's `windowDays` derivation in apps/api/src/routes/regulatory.ts.
// `all` intentionally compares the recent 180 days against all prior history.
const WINDOW_LABEL: Record<RegulatoryFilters['timeframe'], string> = {
  '30d': 'Last 30 days vs prior 30 days',
  '90d': 'Last 90 days vs prior 90 days',
  '365d': 'Last 180 days vs prior 180 days',
  all: 'Recent 180 days vs prior (all-time)',
}

/**
 * P4 — Regulator Velocity.
 *
 * Bar-pair chart keyed by authority (details->>'authority' on the server).
 * SP3 design-system compliance: the outer `section` has
 * `data-chart-shell="true"` — sub-project 4 replaces the shell marker.
 */
export function RegulatorVelocityChart({ filters }: Props) {
  const { data, isLoading, isError } = useRegulatoryVelocity(filters)

  return (
    <section
      data-testid="p4-regulator-velocity"
      data-chart-shell="true"
      className="rounded-md border p-4"
      aria-label="Regulator velocity"
    >
      <header className="mb-3">
        <h2 className="text-lg font-semibold">Regulator velocity</h2>
        <p className="text-sm text-muted-foreground">
          {WINDOW_LABEL[filters.timeframe]}
        </p>
      </header>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : isError ? (
        <div role="alert" className="text-sm text-destructive">
          Unable to load regulator velocity.
        </div>
      ) : !data || data.authorities.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No enforcement actions in the selected timeframe.
        </p>
      ) : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data.authorities}
              margin={{ top: 5, right: 12, left: -10, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid-stroke)" />
              <XAxis
                dataKey="authority"
                tick={{ fontSize: 11, fill: 'var(--chart-axis-label)' }}
                stroke="var(--chart-axis-stroke)"
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fill: 'var(--chart-axis-label)' }}
                stroke="var(--chart-axis-stroke)"
              />
              <Tooltip
                contentStyle={{
                  background: 'var(--surface-elevated)',
                  border: '1px solid var(--border-default)',
                  borderRadius: 6,
                  fontSize: 12,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar
                dataKey="recent"
                name="Recent"
                fill="var(--cat-1)"
                fillOpacity={0.85}
              />
              <Bar
                dataKey="prior"
                name="Prior"
                fill="var(--cat-2)"
                fillOpacity={0.85}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  )
}
